import { tr } from "../i18n/LanguageContext";
import React, { useState, useRef, useEffect } from "react";
import { apiErrorMessage } from "../lib/apiError";
import { useParams } from "react-router-dom";
import { useNavigate } from "../i18n/router";
import { Calendar, MapPin, Clock, Users, Type, FileText, ImageIcon, Globe, Upload, X, Loader2, Euro, CheckCircle, Hourglass } from "lucide-react";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Label } from "../components/ui/label";
import { practiceTypes, cities } from "../data/mock";
import { useEventLanguages } from "../hooks/useEventLanguages";
import { currencies, PRICING_TYPES, pricingTypeOptions } from "../data/currencies";
import { meetingPlatforms } from "../data/meetingPlatforms";
import { useToast } from "../hooks/use-toast";
import { useAuth } from "../contexts/AuthContext";
import ImageCropModal from "../components/ImageCropModal";
import { readFileAsDataURL } from "../utils/cropImage";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AddEventPage = () => {
  const { toast } = useToast();
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  // When the route is /events/:id/edit, this component acts as an EDIT form
  // for that event instead of a CREATE form. Same file, same layout — reduces
  // drift between the two flows.
  const params = useParams();
  const editingEventId = params.id || null;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin" style={{ color: "var(--eunoia-accent)" }} />
      </div>
    );
  }

  if (!user || (user.role !== "organizer" && user.role !== "admin")) {
    return (
      <div className="min-h-screen eunoia-space-lg px-4 pb-16">
        <div className="max-w-2xl mx-auto text-center">
          <div className="flex items-center justify-center gap-3 mb-6">
            <Calendar className="h-8 w-8" style={{ color: "var(--eunoia-accent)" }} />
            <h1 className="eunoia-title-large">{tr("Создать событие")}</h1>
          </div>
          <p className="eunoia-body-large max-w-xl mx-auto mb-8">
            {tr("Поделитесь вашей духовной практикой с сообществом.")}
          </p>
          <div className="eunoia-surface p-8">
            <div className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6" style={{ backgroundColor: "var(--eunoia-accent-whisper)" }}>
              <Users className="h-10 w-10" style={{ color: "var(--eunoia-accent)" }} />
            </div>
            <h2 className="eunoia-title-medium mb-4">{tr("Станьте организатором")}</h2>
            <p className="eunoia-body mb-8">{tr("Для создания событий необходимо стать организатором EUNOIA.")}</p>
            <a
              href={user ? "/organizers/apply" : "/login?redirect=/organizers/apply&intent=organizer_apply"}
              className="eunoia-btn-primary inline-flex items-center gap-2"
              style={{ textDecoration: "none" }}
            >
              {user ? tr("Подать заявку на роль организатора") : tr("Войти и стать организатором")}
            </a>
          </div>
        </div>
      </div>
    );
  }

  return <EventForm user={user} toast={toast} navigate={navigate} editingEventId={editingEventId} />;
};

function EventForm({ user, toast, navigate, editingEventId }) {
  const { eventLanguages, getLanguageLabel } = useEventLanguages();
  const fileInputRef = useRef(null);
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    date: "",
    time: "",
    city: "",
    location: "",
    onlineLink: "",
    meetingPlatform: "other",
    practices: [],
    languages: [],
    type: "offline",
    organizer: user?.displayName || "",
    image_url: "",
    maxAttendees: "",
    isFree: true,
    pricingType: PRICING_TYPES.PAID,
    price: "",
    currency: "EUR",
    registrationDeadline: "",
    approvalMode: "auto",
  });
  // Co-organizers state — array of {userId, displayName, avatarUrl}. Denormalized
  // so we can render chips instantly without re-fetching users. Kept separate from
  // formData because the picker has its own local search/loading state.
  const [coOrganizers, setCoOrganizers] = useState([]);
  const [coOrgQuery, setCoOrgQuery] = useState("");
  const [coOrgResults, setCoOrgResults] = useState([]);
  const [coOrgSearching, setCoOrgSearching] = useState(false);
  const [coOrgDropdownOpen, setCoOrgDropdownOpen] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [cropSrc, setCropSrc] = useState(null);  // data URL fed to crop modal; null = modal closed
  // Session-scoped crop memory: if the user re-opens the modal with the same
  // image bytes (identical data URL), restore their previous crop/zoom. New
  // image ⇒ falls back to defaults automatically.
  const lastCropRef = useRef({ imageSrc: null, crop: { x: 0, y: 0 }, zoom: 1 });
  const [submitting, setSubmitting] = useState(false);
  const [loadingEvent, setLoadingEvent] = useState(!!editingEventId);

  const set = (field, value) => setFormData((p) => ({ ...p, [field]: value }));

  // Debounced organizer search for the "Со-организаторы" picker.
  useEffect(() => {
    const q = coOrgQuery.trim();
    if (!q) {
      setCoOrgResults([]);
      return;
    }
    const excluded = coOrganizers.map((c) => c.userId).join(",");
    const handle = setTimeout(async () => {
      setCoOrgSearching(true);
      try {
        const params = new URLSearchParams({ q });
        if (excluded) params.set("exclude", excluded);
        const res = await fetch(`${BACKEND_URL}/api/organizers/search?${params.toString()}`, { credentials: "include" });
        if (!res.ok) throw new Error("search failed");
        const data = await res.json();
        setCoOrgResults(Array.isArray(data) ? data : []);
      } catch {
        setCoOrgResults([]);
      } finally {
        setCoOrgSearching(false);
      }
    }, 300);
    return () => clearTimeout(handle);
  }, [coOrgQuery, coOrganizers]);

  const addCoOrganizer = (organizer) => {
    if (!organizer?.userId) return;
    setCoOrganizers((prev) => (prev.some((c) => c.userId === organizer.userId) ? prev : [...prev, organizer]));
    setCoOrgQuery("");
    setCoOrgResults([]);
    setCoOrgDropdownOpen(false);
  };
  const removeCoOrganizer = (userId) => {
    setCoOrganizers((prev) => prev.filter((c) => c.userId !== userId));
  };
  const coOrgInitials = (name) => (name || "?").split(" ").map((w) => w[0]).join("").toUpperCase().slice(0, 2);

  // Practice-array helpers (Task 1). Selecting from the dropdown APPENDS to
  // the list; the currently-selected practices render as removable chips
  // directly below the dropdown. This keeps the existing visual language
  // (same Select component) while gaining true multi-select behaviour.
  const addPractice = (value) => {
    if (!value) return;
    setFormData((p) => (p.practices.includes(value) ? p : { ...p, practices: [...p.practices, value] }));
  };
  const removePractice = (value) => {
    setFormData((p) => ({ ...p, practices: p.practices.filter((x) => x !== value) }));
  };
  const practiceLabel = (slug) => tr(practiceTypes.find((pt) => pt.value === slug)?.label || slug);

  // Task 6: when editing, hydrate the form from the existing event.
  useEffect(() => {
    if (!editingEventId) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/events/${editingEventId}`, { credentials: "include" });
        if (!res.ok) throw new Error("not found");
        const ev = await res.json();
        if (cancelled) return;
        // Prefill: prefer the new `practices` array, fall back to legacy single `practice`.
        const practicesArr = Array.isArray(ev.practices) && ev.practices.length
          ? ev.practices
          : (ev.practice ? [ev.practice] : []);
        setFormData({
          title: ev.title || "",
          description: ev.description || "",
          date: ev.date || "",
          time: ev.time || "",
          city: ev.city || "",
          location: ev.location || "",
          onlineLink: ev.onlineLink || "",
          meetingPlatform: ev.meetingPlatform || "other",
          practices: practicesArr,
          languages: Array.isArray(ev.languages) ? ev.languages : [],
          type: ev.type || "offline",
          organizer: ev.organizer || user?.displayName || "",
          image_url: ev.image || "",
          maxAttendees: ev.maxAttendees != null ? String(ev.maxAttendees) : "",
          isFree: !!ev.isFree,
          // Prefer explicit pricingType from the doc; fall back to legacy
          // isFree/price heuristic so old events open in the right mode.
          pricingType: ev.pricingType
            || (ev.isFree === false && ev.price != null && Number(ev.price) > 0
                ? PRICING_TYPES.PAID
                : PRICING_TYPES.FREE),
          price: ev.price != null ? String(ev.price) : "",
          currency: ev.currency || "EUR",
          registrationDeadline: ev.registrationDeadline || "",
          approvalMode: ev.approvalMode || "auto",
        });
        if (ev.image) setImagePreview(ev.image);
        // Hydrate co-organizers from denormalized snapshot on the event doc.
        if (Array.isArray(ev.coOrganizers) && ev.coOrganizers.length) {
          setCoOrganizers(ev.coOrganizers);
        } else {
          setCoOrganizers([]);
        }
      } catch (e) {
        toast({ title: tr("Не удалось загрузить событие"), description: tr("Возможно, оно было удалено."), variant: "destructive" });
        navigate("/my-events");
      } finally {
        if (!cancelled) setLoadingEvent(false);
      }
    })();
    return () => { cancelled = true; };
  }, [editingEventId]);

  const handleImageSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const ext = file.name.split(".").pop().toLowerCase();
    if (!["jpg", "jpeg", "png", "webp"].includes(ext)) {
      toast({ title: tr("Неподдерживаемый формат"), description: tr("Допустимые форматы: jpg, jpeg, png, webp"), variant: "destructive" });
      if (fileInputRef.current) fileInputRef.current.value = "";
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast({ title: tr("Файл слишком большой"), description: tr("Максимальный размер: 5 МБ"), variant: "destructive" });
      if (fileInputRef.current) fileInputRef.current.value = "";
      return;
    }

    // Open the crop modal with the raw file. Nothing is uploaded until the
    // organizer confirms — cancelling avoids orphaned uploads on the server.
    try {
      const dataUrl = await readFileAsDataURL(file);
      setCropSrc(dataUrl);
    } catch {
      toast({ title: tr("Не удалось прочитать файл"), variant: "destructive" });
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleCropCancel = () => {
    setCropSrc(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleCropConfirm = async (croppedBlob) => {
    setUploading(true);
    // Revoke previous object URL to avoid a leak on repeated crops.
    let previousObjectUrl = null;
    if (imagePreview && imagePreview.startsWith("blob:")) previousObjectUrl = imagePreview;
    try {
      // Local preview from the just-cropped blob so the organizer sees
      // exactly what will be saved.
      const previewUrl = URL.createObjectURL(croppedBlob);
      setImagePreview(previewUrl);
      if (previousObjectUrl) URL.revokeObjectURL(previousObjectUrl);

      const fd = new FormData();
      fd.append("file", new File([croppedBlob], "event.jpg", { type: croppedBlob.type || "image/jpeg" }));
      const res = await fetch(`${BACKEND_URL}/api/upload/image`, {
        method: "POST",
        body: fd,
        credentials: "include",
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(apiErrorMessage(err, tr("Ошибка загрузки")));
      }
      const data = await res.json();
      const fullUrl = data.url.startsWith("http") ? data.url : `${BACKEND_URL}${data.url}`;
      set("image_url", fullUrl);
      toast({ title: tr("Изображение загружено") });
      setCropSrc(null);
    } catch (err) {
      toast({ title: tr("Ошибка загрузки"), description: err.message, variant: "destructive" });
      setImagePreview(null);
      setCropSrc(null);
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const removeImage = () => {
    setImagePreview(null);
    set("image_url", "");
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.practices.length) {
      toast({ title: tr("Выберите хотя бы один тип практики"), variant: "destructive" });
      return;
    }
    if (!formData.languages.length) {
      toast({ title: tr("Выберите хотя бы один язык мероприятия"), variant: "destructive" });
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        title: formData.title,
        description: formData.description,
        date: formData.date,
        time: formData.time,
        city: formData.type === "online" ? "Онлайн" : formData.city,
        location: formData.location,
        onlineLink: formData.onlineLink,
        meetingPlatform:
          formData.type === "online" || formData.type === "hybrid"
            ? formData.meetingPlatform || "other"
            : null,
        // Legacy `practice` field kept in sync with the first entry for
        // backward-compat with callers that still read it (event card badges,
        // some old filter code paths).
        practice: formData.practices[0],
        practices: formData.practices,
        languages: formData.languages,
        type: formData.type,
        organizer: formData.organizer,
        coOrganizerIds: coOrganizers.map((c) => c.userId),
        image: formData.image_url,
        maxAttendees: formData.maxAttendees ? parseInt(formData.maxAttendees, 10) : null,
        // Send both pricingType (canonical) AND isFree (legacy) so older code
        // paths keep working. isFree=true when the event is not a paid one.
        pricingType: formData.pricingType,
        isFree: formData.pricingType !== PRICING_TYPES.PAID,
        price: formData.pricingType === PRICING_TYPES.PAID ? parseFloat(formData.price || 0) : null,
        currency: formData.currency,
        registrationDeadline: formData.registrationDeadline || null,
        approvalMode: formData.approvalMode,
      };
      const url = editingEventId
        ? `${BACKEND_URL}/api/events/${editingEventId}`
        : `${BACKEND_URL}/api/events`;
      const method = editingEventId ? "PATCH" : "POST";
      const res = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
        credentials: "include",
      });
      if (res.ok) {
        const savedEvent = await res.json().catch(() => null);
        if (editingEventId) {
          toast({ title: tr("Событие обновлено"), description: tr("Участники получат уведомление об изменениях.") });
          navigate(`/events/${editingEventId}`);
        } else {
          toast({ title: tr("Событие создано!"), description: tr("Ваше событие было успешно добавлено.") });
          // Land the organizer on their new event page — not the general list.
          navigate(savedEvent?.id ? `/events/${savedEvent.id}` : "/events");
        }
      } else {
        const err = await res.json();
        toast({ title: tr("Ошибка"), description: apiErrorMessage(err, tr("Не удалось сохранить событие")), variant: "destructive" });
      }
    } catch {
      toast({ title: tr("Ошибка"), description: tr("Произошла ошибка при сохранении события"), variant: "destructive" });
    } finally {
      setSubmitting(false);
    }
  };

  const filteredCities = cities.filter((c) => c !== "Все города" && c !== "Онлайн");

  return (
    <div className="min-h-screen eunoia-space-lg px-4 pb-16">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="text-center mb-10">
          <h1 className="eunoia-title-large mb-3" data-testid="event-form-heading">
            {editingEventId ? tr("Редактировать событие") : tr("Создать событие")}
          </h1>
          <p className="eunoia-body-large">
            {editingEventId
              ? tr("Изменения увидят все зарегистрированные участники.")
              : tr("Поделитесь своим знанием и создайте событие для сообщества")}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Basic Info */}
          <div className="eunoia-surface p-6 space-y-5">
            <h2 className="eunoia-title-small flex items-center gap-2 mb-2">
              <Calendar className="h-5 w-5" style={{ color: "var(--eunoia-accent)" }} />
              {tr("Основная информация")}
            </h2>

            <div className="space-y-2">
              <Label htmlFor="title" className="flex items-center gap-2 text-sm font-medium" style={{ color: "var(--eunoia-deep)" }}>
                <Type className="h-4 w-4" style={{ color: "var(--eunoia-accent)" }} /> {tr("Название события")}
              </Label>
              <input id="title" value={formData.title} onChange={(e) => set("title", e.target.value)} className="eunoia-input" placeholder={tr("Медитация полнолуния")} required />
            </div>

            <div className="space-y-2">
              <Label htmlFor="description" className="flex items-center gap-2 text-sm font-medium" style={{ color: "var(--eunoia-deep)" }}>
                <FileText className="h-4 w-4" style={{ color: "var(--eunoia-accent)" }} /> {tr("Описание")}
              </Label>
              <textarea id="description" value={formData.description} onChange={(e) => set("description", e.target.value)} className="eunoia-input min-h-[120px] resize-y" placeholder={tr("Подробное описание события...")} required />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="date" className="text-sm font-medium" style={{ color: "var(--eunoia-deep)" }}>{tr("Дата")}</Label>
                <input id="date" type="date" value={formData.date} onChange={(e) => set("date", e.target.value)} className="eunoia-input" required />
              </div>
              <div className="space-y-2">
                <Label htmlFor="time" className="flex items-center gap-2 text-sm font-medium" style={{ color: "var(--eunoia-deep)" }}>
                  <Clock className="h-4 w-4" style={{ color: "var(--eunoia-accent)" }} /> {tr("Время")}
                </Label>
                <input id="time" type="time" value={formData.time} onChange={(e) => set("time", e.target.value)} className="eunoia-input" required />
              </div>
            </div>
          </div>

          {/* Event Type */}
          <div className="eunoia-surface p-6 space-y-4">
            <h2 className="eunoia-title-small mb-2">{tr("Тип события")}</h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {[
                { key: "offline", label: tr("Оффлайн"), desc: tr("Физическое присутствие"), icon: MapPin, color: "var(--eunoia-accent)" },
                { key: "online", label: tr("Онлайн"), desc: tr("Только удаленно"), icon: Globe, color: "#0ea5e9" },
                { key: "hybrid", label: tr("Гибрид"), desc: tr("Онлайн + Оффлайн"), icon: Users, color: "#8b5cf6" },
              ].map((t) => (
                <button
                  key={t.key}
                  type="button"
                  data-testid={`event-type-${t.key}`}
                  onClick={() => {
                    set("type", t.key);
                    if (t.key === "online") set("city", "Онлайн");
                  }}
                  className="p-4 rounded-xl border-2 transition-all text-left"
                  style={{
                    borderColor: formData.type === t.key ? t.color : "var(--eunoia-whisper)",
                    backgroundColor: formData.type === t.key ? `${t.color}08` : "transparent",
                  }}
                >
                  <t.icon className="h-6 w-6 mb-2" style={{ color: t.color }} />
                  <p className="font-medium text-sm" style={{ color: "var(--eunoia-deep)" }}>{tr(t.label)}</p>
                  <p className="text-xs mt-0.5" style={{ color: "var(--eunoia-soft)" }}>{t.desc}</p>
                </button>
              ))}
            </div>
          </div>

          {/* Location */}
          {formData.type !== "online" && (
            <div className="eunoia-surface p-6 space-y-4">
              <h2 className="eunoia-title-small flex items-center gap-2 mb-2">
                <MapPin className="h-5 w-5" style={{ color: "var(--eunoia-accent)" }} /> {tr("Место проведения")}
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-sm font-medium" style={{ color: "var(--eunoia-deep)" }}>{tr("Город")}</Label>
                  <Select value={formData.city} onValueChange={(v) => set("city", v)}>
                    <SelectTrigger className="eunoia-input"><SelectValue placeholder={tr("Выберите город")} /></SelectTrigger>
                    <SelectContent>{[...filteredCities].sort((a, b) => tr(a).localeCompare(tr(b))).map((c) => <SelectItem key={c} value={c}>{tr(c)}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-sm font-medium" style={{ color: "var(--eunoia-deep)" }}>{tr("Адрес")}</Label>
                  <input value={formData.location} onChange={(e) => set("location", e.target.value)} className="eunoia-input" placeholder={tr("Адрес или название места")} required={formData.type !== "online"} />
                </div>
              </div>
            </div>
          )}

          {/* Online Meeting (Online + Hybrid) */}
          {(formData.type === "online" || formData.type === "hybrid") && (
            <div className="eunoia-surface p-6 space-y-5" data-testid="online-meeting-section">
              <h2 className="eunoia-title-small flex items-center gap-2 mb-2">
                <Globe className="h-5 w-5" style={{ color: "#0ea5e9" }} /> {tr("Онлайн встреча")}
              </h2>

              <div className="space-y-2">
                <Label className="text-sm font-medium" style={{ color: "var(--eunoia-deep)" }}>
                  {tr("Платформа встречи")}
                </Label>
                <Select
                  value={formData.meetingPlatform}
                  onValueChange={(v) => set("meetingPlatform", v)}
                >
                  <SelectTrigger className="eunoia-input" data-testid="meeting-platform-trigger">
                    <SelectValue placeholder={tr("Выберите платформу")} />
                  </SelectTrigger>
                  <SelectContent data-testid="meeting-platform-dropdown">
                    {meetingPlatforms.map((p) => (
                      <SelectItem
                        key={p.key}
                        value={p.key}
                        data-testid={`meeting-platform-${p.key}`}
                      >
                        {tr(p.label)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label className="flex items-center gap-2 text-sm font-medium" style={{ color: "var(--eunoia-deep)" }}>
                  <Globe className="h-4 w-4" style={{ color: "#0ea5e9" }} /> {tr("Ссылка на встречу")}
                </Label>
                <input
                  value={formData.onlineLink}
                  onChange={(e) => set("onlineLink", e.target.value)}
                  className="eunoia-input"
                  placeholder="https://..."
                  required={formData.type === "online" || formData.type === "hybrid"}
                  data-testid="meeting-link-input"
                />
                <p className="text-xs" style={{ color: "var(--eunoia-soft)" }}>
                  {tr("Вставьте ссылку на встречу для любой платформы (Telegram, Zoom, Google Meet, Microsoft Teams, Jitsi, Discord и т.д.)")}
                </p>
              </div>
            </div>
          )}

          {/* Practice Types (multi-select) */}
          <div className="eunoia-surface p-6 space-y-3">
            <Label className="text-sm font-medium" style={{ color: "var(--eunoia-deep)" }}>{tr("Типы практик")}</Label>
            <p className="text-xs" style={{ color: "var(--eunoia-soft)" }}>
              {tr("Можно выбрать несколько — событие появится в фильтрах каждого выбранного типа.")}
            </p>
            <Select value="" onValueChange={(v) => addPractice(v)}>
              <SelectTrigger className="eunoia-input" data-testid="practice-type-trigger">
                <SelectValue placeholder={formData.practices.length ? tr("Добавить ещё одну практику") : tr("Выберите тип практики")} />
              </SelectTrigger>
              <SelectContent data-testid="practice-type-dropdown">
                {practiceTypes
                  .filter((p) => !formData.practices.includes(p.value))
                  .map((p) => (
                    <SelectItem key={p.value} value={p.value} data-testid={`practice-${p.value}`}>{tr(p.label)}</SelectItem>
                  ))}
              </SelectContent>
            </Select>
            {formData.practices.length > 0 && (
              <div className="flex flex-wrap gap-2 pt-1" data-testid="selected-practices">
                {formData.practices.map((slug) => (
                  <span
                    key={slug}
                    className="inline-flex items-center gap-2 pl-3 pr-1 py-1 rounded-full text-sm"
                    style={{ backgroundColor: "var(--eunoia-accent-whisper)", color: "var(--eunoia-accent)" }}
                    data-testid={`practice-chip-${slug}`}
                  >
                    {practiceLabel(slug)}
                    <button
                      type="button"
                      onClick={() => removePractice(slug)}
                      aria-label={`${tr("Удалить")} ${practiceLabel(slug)}`}
                      className="h-5 w-5 inline-flex items-center justify-center rounded-full hover:bg-white/40"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Event languages (multi-select, required) */}
          <div className="eunoia-surface p-6 space-y-3">
            <Label className="text-sm font-medium" style={{ color: "var(--eunoia-deep)" }}>{tr("Язык(и) мероприятия")}</Label>
            <p className="text-xs" style={{ color: "var(--eunoia-soft)" }}>
              {tr("Можно выбрать несколько — например, для двуязычного события.")}
            </p>
            {formData.languages.length < eventLanguages.length && (
              <Select value="" onValueChange={(v) => setFormData((p) => (p.languages.includes(v) ? p : { ...p, languages: [...p.languages, v] }))}>
                <SelectTrigger className="eunoia-input" data-testid="language-trigger">
                  <SelectValue placeholder={formData.languages.length ? tr("Добавить ещё один язык") : tr("Выберите язык")} />
                </SelectTrigger>
                <SelectContent data-testid="language-dropdown">
                  {eventLanguages
                    .filter((l) => !formData.languages.includes(l.value))
                    .map((l) => (
                      <SelectItem key={l.value} value={l.value} data-testid={`language-${l.value}`}>{tr(l.label)}</SelectItem>
                    ))}
                </SelectContent>
              </Select>
            )}
            {formData.languages.length > 0 && (
              <div className="flex flex-wrap gap-2 pt-1" data-testid="selected-languages">
                {formData.languages.map((code) => (
                  <span
                    key={code}
                    className="inline-flex items-center gap-2 pl-3 pr-1 py-1 rounded-full text-sm"
                    style={{ backgroundColor: "var(--eunoia-accent-whisper)", color: "var(--eunoia-accent)" }}
                    data-testid={`language-chip-${code}`}
                  >
                    {getLanguageLabel(code)}
                    <button
                      type="button"
                      onClick={() => setFormData((p) => ({ ...p, languages: p.languages.filter((x) => x !== code) }))}
                      aria-label={`${tr("Удалить")} ${getLanguageLabel(code)}`}
                      data-testid={`language-chip-remove-${code}`}
                      className="h-5 w-5 inline-flex items-center justify-center rounded-full hover:bg-white/40"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Organizer */}
          <div className="eunoia-surface p-6 space-y-2">
            <Label className="flex items-center gap-2 text-sm font-medium" style={{ color: "var(--eunoia-deep)" }}>
              <Users className="h-4 w-4" style={{ color: "var(--eunoia-accent)" }} /> {tr("Организатор")}
            </Label>
            <input value={formData.organizer} onChange={(e) => set("organizer", e.target.value)} className="eunoia-input" placeholder={tr("Ваше имя")} required />
          </div>

          {/* Co-organizers */}
          <div className="eunoia-surface p-6 space-y-3" data-testid="co-organizers-section">
            <Label className="flex items-center gap-2 text-sm font-medium" style={{ color: "var(--eunoia-deep)" }}>
              <Users className="h-4 w-4" style={{ color: "var(--eunoia-accent)" }} /> {tr("Со-организаторы")}
            </Label>
            <p className="text-xs" style={{ color: "var(--eunoia-soft)" }}>
              {tr("Проводите событие вместе — добавьте других организаторов. Они смогут редактировать событие, видеть участников и получать все уведомления.")}
            </p>
            <div className="relative">
              <input
                type="text"
                value={coOrgQuery}
                onChange={(e) => { setCoOrgQuery(e.target.value); setCoOrgDropdownOpen(true); }}
                onFocus={() => setCoOrgDropdownOpen(true)}
                onBlur={() => setTimeout(() => setCoOrgDropdownOpen(false), 150)}
                className="eunoia-input"
                placeholder={tr("Начните вводить имя организатора...")}
                data-testid="co-organizer-search-input"
              />
              {coOrgDropdownOpen && coOrgQuery.trim() && (
                <div
                  className="absolute z-10 left-0 right-0 mt-1 rounded-xl border shadow-md overflow-hidden"
                  style={{ backgroundColor: "var(--eunoia-pure)", borderColor: "var(--eunoia-whisper)" }}
                  data-testid="co-organizer-search-dropdown"
                >
                  {coOrgSearching ? (
                    <div className="px-4 py-3 text-sm" style={{ color: "var(--eunoia-soft)" }}>{tr("Ищу...")}</div>
                  ) : coOrgResults.length === 0 ? (
                    <div className="px-4 py-3 text-sm" style={{ color: "var(--eunoia-soft)" }} data-testid="co-organizer-no-results">{tr("Ничего не найдено")}</div>
                  ) : (
                    coOrgResults.map((r) => (
                      <button
                        key={r.userId}
                        type="button"
                        onMouseDown={(e) => { e.preventDefault(); addCoOrganizer(r); }}
                        onClick={(e) => { e.preventDefault(); addCoOrganizer(r); }}
                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); addCoOrganizer(r); } }}
                        className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-[color:var(--eunoia-glow)] transition-colors"
                        data-testid={`co-organizer-result-${r.userId}`}
                      >
                        {r.avatarUrl ? (
                          <img src={r.avatarUrl} alt="" className="h-8 w-8 rounded-full object-cover flex-shrink-0" />
                        ) : (
                          <span
                            className="h-8 w-8 rounded-full flex items-center justify-center text-xs font-medium flex-shrink-0"
                            style={{ backgroundColor: "var(--eunoia-accent-whisper)", color: "var(--eunoia-accent)" }}
                          >
                            {coOrgInitials(r.displayName)}
                          </span>
                        )}
                        <span className="flex-1 min-w-0">
                          <span className="block text-sm font-medium truncate" style={{ color: "var(--eunoia-deep)" }}>{r.displayName}</span>
                          {r.city && <span className="block text-xs truncate" style={{ color: "var(--eunoia-soft)" }}>{r.city}</span>}
                        </span>
                      </button>
                    ))
                  )}
                </div>
              )}
            </div>
            {coOrganizers.length > 0 && (
              <div className="flex flex-wrap gap-2 pt-1" data-testid="selected-co-organizers">
                {coOrganizers.map((c) => (
                  <span
                    key={c.userId}
                    className="inline-flex items-center gap-2 pl-1 pr-1 py-1 rounded-full text-sm"
                    style={{ backgroundColor: "var(--eunoia-accent-whisper)", color: "var(--eunoia-accent)" }}
                    data-testid={`co-organizer-chip-${c.userId}`}
                  >
                    {c.avatarUrl ? (
                      <img src={c.avatarUrl} alt="" className="h-6 w-6 rounded-full object-cover" />
                    ) : (
                      <span
                        className="h-6 w-6 rounded-full flex items-center justify-center text-[10px] font-medium"
                        style={{ backgroundColor: "var(--eunoia-pure)", color: "var(--eunoia-accent)" }}
                      >
                        {coOrgInitials(c.displayName)}
                      </span>
                    )}
                    <span className="pl-1">{c.displayName}</span>
                    <button
                      type="button"
                      onClick={() => removeCoOrganizer(c.userId)}
                      aria-label={`${tr("Удалить")} ${c.displayName}`}
                      className="h-5 w-5 inline-flex items-center justify-center rounded-full hover:bg-white/40"
                      data-testid={`co-organizer-remove-${c.userId}`}
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Participation Settings */}
          <div className="eunoia-surface p-6 space-y-5" data-testid="participation-settings">
            <h2 className="eunoia-title-small flex items-center gap-2 mb-2">
              <Users className="h-5 w-5" style={{ color: "var(--eunoia-accent)" }} />
              {tr("Участие и запись")}
            </h2>

            {/* Pricing Type — Paid / Free / Donation */}
            <div>
              <Label className="text-sm font-medium mb-3 block" style={{ color: "var(--eunoia-deep)" }}>
                {tr("Тип оплаты")}
              </Label>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="pricing-type-selector">
                {pricingTypeOptions.map((opt) => {
                  const isActive = formData.pricingType === opt.value;
                  const testMap = {
                    [PRICING_TYPES.PAID]: "pricing-type-paid",
                    [PRICING_TYPES.FREE]: "pricing-type-free",
                    [PRICING_TYPES.DONATION]: "pricing-type-donation",
                  };
                  const iconMap = {
                    [PRICING_TYPES.PAID]: <Euro className="h-5 w-5 mb-2" style={{ color: "var(--eunoia-accent)" }} />,
                    [PRICING_TYPES.FREE]: <CheckCircle className="h-5 w-5 mb-2" style={{ color: "var(--eunoia-accent)" }} />,
                    [PRICING_TYPES.DONATION]: <Hourglass className="h-5 w-5 mb-2" style={{ color: "var(--eunoia-accent)" }} />,
                  };
                  const hintMap = {
                    [PRICING_TYPES.PAID]: tr("Фиксированная стоимость и валюта"),
                    [PRICING_TYPES.FREE]: tr("Свободный вход, без оплаты"),
                    [PRICING_TYPES.DONATION]: tr("Каждый вносит по своим возможностям"),
                  };
                  return (
                    <button
                      key={opt.value}
                      type="button"
                      data-testid={testMap[opt.value]}
                      onClick={() => set("pricingType", opt.value)}
                      className="p-4 rounded-xl border-2 transition-all text-left"
                      style={{
                        borderColor: isActive ? "var(--eunoia-accent)" : "var(--eunoia-whisper)",
                        backgroundColor: isActive ? "var(--eunoia-accent-whisper)" : "transparent",
                      }}
                      aria-pressed={isActive}
                    >
                      {iconMap[opt.value]}
                      <p className="font-medium text-sm" style={{ color: "var(--eunoia-deep)" }}>{tr(opt.label)}</p>
                      <p className="text-xs mt-0.5" style={{ color: "var(--eunoia-soft)" }}>{hintMap[opt.value]}</p>
                    </button>
                  );
                })}
              </div>
            </div>

            {formData.pricingType === PRICING_TYPES.PAID && (
              <div className="grid grid-cols-2 gap-4" data-testid="paid-amount-fields">
                <div className="space-y-2">
                  <Label className="text-sm font-medium" style={{ color: "var(--eunoia-deep)" }}>{tr("Стоимость")}</Label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={formData.price}
                    onChange={(e) => set("price", e.target.value)}
                    className="eunoia-input"
                    placeholder="25.00"
                    data-testid="event-price-input"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-sm font-medium" style={{ color: "var(--eunoia-deep)" }}>{tr("Валюта")}</Label>
                  <Select value={formData.currency} onValueChange={(v) => set("currency", v)}>
                    <SelectTrigger className="eunoia-input" data-testid="event-currency-trigger"><SelectValue /></SelectTrigger>
                    <SelectContent data-testid="event-currency-dropdown">
                      {currencies.map((c) => (
                        <SelectItem key={c.code} value={c.code} data-testid={`currency-${c.code}`}>{tr(c.label)}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}

            {/* Capacity + Deadline */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="flex items-center gap-2 text-sm font-medium" style={{ color: "var(--eunoia-deep)" }}>
                  <Users className="h-4 w-4" style={{ color: "var(--eunoia-accent)" }} /> {tr("Максимум мест")}
                </Label>
                <input
                  type="number"
                  min="1"
                  value={formData.maxAttendees}
                  onChange={(e) => set("maxAttendees", e.target.value)}
                  className="eunoia-input"
                  placeholder={tr("Без ограничения")}
                  data-testid="event-capacity-input"
                />
                <p className="text-xs" style={{ color: "var(--eunoia-soft)" }}>{tr("Оставьте пустым для неограниченного участия")}</p>
              </div>
              <div className="space-y-2">
                <Label className="flex items-center gap-2 text-sm font-medium" style={{ color: "var(--eunoia-deep)" }}>
                  <Hourglass className="h-4 w-4" style={{ color: "var(--eunoia-accent)" }} /> {tr("Дедлайн регистрации")}
                </Label>
                <input
                  type="date"
                  value={formData.registrationDeadline}
                  onChange={(e) => set("registrationDeadline", e.target.value)}
                  className="eunoia-input"
                  data-testid="event-deadline-input"
                />
                <p className="text-xs" style={{ color: "var(--eunoia-soft)" }}>{tr("После этой даты запись закроется")}</p>
              </div>
            </div>

            {/* Approval mode */}
            <div className="space-y-2">
              <Label className="text-sm font-medium" style={{ color: "var(--eunoia-deep)" }}>{tr("Режим подтверждения")}</Label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <button
                  type="button"
                  data-testid="approval-auto-btn"
                  onClick={() => set("approvalMode", "auto")}
                  className="p-3 rounded-xl border-2 transition-all text-left"
                  style={{
                    borderColor: formData.approvalMode === "auto" ? "var(--eunoia-accent)" : "var(--eunoia-whisper)",
                    backgroundColor: formData.approvalMode === "auto" ? "var(--eunoia-accent-whisper)" : "transparent",
                  }}
                >
                  <p className="font-medium text-sm" style={{ color: "var(--eunoia-deep)" }}>{tr("Автоматически")}</p>
                  <p className="text-xs mt-0.5" style={{ color: "var(--eunoia-soft)" }}>{tr("Любой может записаться")}</p>
                </button>
                <button
                  type="button"
                  data-testid="approval-manual-btn"
                  onClick={() => set("approvalMode", "manual")}
                  className="p-3 rounded-xl border-2 transition-all text-left"
                  style={{
                    borderColor: formData.approvalMode === "manual" ? "var(--eunoia-accent)" : "var(--eunoia-whisper)",
                    backgroundColor: formData.approvalMode === "manual" ? "var(--eunoia-accent-whisper)" : "transparent",
                  }}
                >
                  <p className="font-medium text-sm" style={{ color: "var(--eunoia-deep)" }}>{tr("По заявке")}</p>
                  <p className="text-xs mt-0.5" style={{ color: "var(--eunoia-soft)" }}>{tr("Вы подтверждаете каждого участника")}</p>
                </button>
              </div>
            </div>
          </div>

          {/* Image Upload */}
          <div className="eunoia-surface p-6 space-y-4" data-testid="image-upload-section">
            <Label className="flex items-center gap-2 text-sm font-medium" style={{ color: "var(--eunoia-deep)" }}>
              <ImageIcon className="h-4 w-4" style={{ color: "var(--eunoia-accent)" }} /> {tr("Изображение события")}
            </Label>

            {imagePreview ? (
              <div className="relative rounded-xl overflow-hidden" data-testid="image-preview">
                <img src={imagePreview} alt="Preview" className="w-full h-48 object-cover rounded-xl" />
                {uploading && (
                  <div className="absolute inset-0 bg-black/40 flex items-center justify-center rounded-xl">
                    <Loader2 className="h-8 w-8 text-white animate-spin" />
                  </div>
                )}
                {!uploading && (
                  <button
                    type="button"
                    onClick={removeImage}
                    data-testid="remove-image-button"
                    className="absolute top-3 right-3 w-8 h-8 rounded-full bg-black/50 hover:bg-black/70 flex items-center justify-center transition-colors"
                  >
                    <X className="h-4 w-4 text-white" />
                  </button>
                )}
                {formData.image_url && !uploading && (
                  <div className="absolute bottom-3 left-3 px-3 py-1 rounded-full text-xs font-medium bg-green-500/90 text-white">
                    {tr("Загружено")}
                  </div>
                )}
              </div>
            ) : (
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                data-testid="upload-image-button"
                className="w-full border-2 border-dashed rounded-xl p-8 text-center transition-all hover:border-opacity-80"
                style={{ borderColor: "var(--eunoia-accent)", backgroundColor: "var(--eunoia-accent-whisper)" }}
              >
                <Upload className="h-8 w-8 mx-auto mb-3" style={{ color: "var(--eunoia-accent)" }} />
                <p className="text-sm font-medium" style={{ color: "var(--eunoia-deep)" }}>{tr("Нажмите, чтобы выбрать изображение")}</p>
                <p className="text-xs mt-1" style={{ color: "var(--eunoia-soft)" }}>{tr("JPG, PNG или WebP, до 5 МБ")}</p>
              </button>
            )}

            <input
              ref={fileInputRef}
              type="file"
              accept=".jpg,.jpeg,.png,.webp"
              onChange={handleImageSelect}
              className="hidden"
              data-testid="image-file-input"
            />
          </div>

          {/* Submit */}
          <button type="submit" disabled={submitting || uploading || loadingEvent} className="eunoia-btn-primary w-full text-lg py-4 flex items-center justify-center gap-2" data-testid="submit-event-button">
            {submitting ? <Loader2 className="h-5 w-5 animate-spin" /> : (editingEventId ? tr("Сохранить изменения") : tr("Создать событие"))}
          </button>
        </form>

        {/* Guidelines */}
        <div className="eunoia-surface-soft p-6 mt-8">
          <h3 className="eunoia-title-small mb-4" style={{ color: "var(--eunoia-accent)" }}>{tr("Рекомендации")}</h3>
          <ul className="space-y-2 eunoia-body">
            <li>{tr("Используйте ясное и привлекательное название")}</li>
            <li>{tr("Подробно опишите, что будет происходить на событии")}</li>
            <li>{tr("Добавьте качественное изображение для привлечения внимания")}</li>
            <li>{tr("Укажите контактную информацию для вопросов")}</li>
          </ul>
        </div>
      </div>
      {cropSrc && (
        <ImageCropModal
          imageSrc={cropSrc}
          aspect={16 / 9}
          cropShape="rect"
          title={tr("Обрежьте изображение события")}
          onCancel={handleCropCancel}
          onConfirm={handleCropConfirm}
          confirming={uploading}
          initialCrop={lastCropRef.current.imageSrc === cropSrc ? lastCropRef.current.crop : { x: 0, y: 0 }}
          initialZoom={lastCropRef.current.imageSrc === cropSrc ? lastCropRef.current.zoom : 1}
          onStateChange={({ crop, zoom }) => { lastCropRef.current = { imageSrc: cropSrc, crop, zoom }; }}
        />
      )}
    </div>
  );
}

export default AddEventPage;
